import { type AxiosInstance } from 'axios';

import { getApiClient } from '@treneo/infrastructure/api';
import { logger } from '@treneo/shared/logger';

import type { LogoutHandler } from './data/types';
import { AxiosConfigurator, type GetIsLoggedIn } from './infrastructure/axios-configurator';

let axiosConfigurator: AxiosConfigurator | null = null;

export const createAxiosConfigurator = async (
  logoutHandler: LogoutHandler,
  getIsLoggedIn?: GetIsLoggedIn
): Promise<AxiosConfigurator> => {
  axiosConfigurator = new AxiosConfigurator(logoutHandler, getIsLoggedIn);
  await axiosConfigurator.initializeInterceptors();
  return axiosConfigurator;
};

export const setupInstanceInterceptors = async (
  axiosInstance: AxiosInstance,
  logoutHandler: LogoutHandler
): Promise<AxiosInstance> => {
  axiosConfigurator ??= await createAxiosConfigurator(logoutHandler);
  return axiosConfigurator.setupInstanceInterceptors(axiosInstance);
};

export const setupApiClientInterceptors = async (
  logoutHandler: LogoutHandler,
  getIsLoggedIn?: GetIsLoggedIn
): Promise<void> => {
  if (!logoutHandler) {
    logger.warn('No logoutHandler provided to setupApiClientInterceptors');
    return;
  }

  const configurator = (axiosConfigurator ??= await createAxiosConfigurator(
    logoutHandler,
    getIsLoggedIn
  ));
  const apiClient = getApiClient();
  try {
    await apiClient.setupStoreInterceptors(logoutHandler, async instance =>
      configurator.setupInstanceInterceptors(instance)
    );
  } catch (error) {
    logger.warn('Failed to setup API client store interceptors:', error);
  }
};

export const getCurrentConfigurator = (): AxiosConfigurator | null => {
  return axiosConfigurator;
};

export const clearCsrfTokenCache = (): void => {
  axiosConfigurator?.clearTokenCache();
};

export const clearRefreshState = (): void => {
  axiosConfigurator?.clearRefreshState();
};

export const resetLogoutState = (): void => {
  axiosConfigurator?.resetLogoutState();
};
